import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { AlertMarkers } from "./AlertMarkers";
import { FieldRow } from "./DetailGrid";
import { Money } from "./Money";
import { ExternalMono, Mono } from "./Mono";
import { RelatedLink } from "./RelatedLink";
import { StatusBadge } from "./StatusBadge";
import { Timestamp } from "./Timestamp";
import { ErrorState, LoadingState } from "./states";
import { AdminApiError } from "@/api/client";
import { fetchWalletMigrationRow } from "@/api/walletMigrations";
import { useAuth } from "@/auth/useAuth";
import { COLLECTIONS } from "@/firestore/collections";
import {
  DOCUMENT_ID,
  useFirestoreDoc,
  useFirestoreInQuery,
  useFirestoreQuery,
} from "@/firestore/hooks";
import type {
  EarnOperationDoc,
  HoldDoc,
  SessionKeyDoc,
  SettlementDoc,
  SettlementObligationDoc,
  WalletMigrationDoc,
  WalletMigrationDrainDoc,
  WithDocId,
} from "@/firestore/types";
import { isPermissionError } from "@/lib/errors";
import { txUrl } from "@/lib/explorer";
import { useNow } from "@/lib/useNow";
import {
  DRAIN_STATE_LABELS,
  approvalBatchCounts,
  approvalBatchRows,
  cardCohortMarkers,
  drainMarkers,
  drainVaultStatus,
  inventoryObligationLocations,
  isOpenObligation,
  liveSettlements,
  obligationPositionMicroUsdc,
} from "@/wallet/migrationCohorts";
import type { ObligationLocation } from "@/wallet/migrationCohorts";

type Migration = WithDocId<WalletMigrationDoc>;

const RELATION_COLOR = { source: "blue", successor: "teal", other: "gray" } as const;

/**
 * The migration's full approval batch (`approvalSessionKeys`, wallet-service 0.77.0): the transfer
 * keys, the source's forward keys (0.78.0) and the successor's card keys, each with its live state
 * (`sessionKeys`, `documentId() in`). The owner signs their install prompts in one biometric batch.
 */
export function ApprovalBatchPanel({ migration }: { migration: Migration }) {
  const ids = (migration.approvalSessionKeys ?? []).map((k) => k.sessionKeyId);
  const keys = useFirestoreInQuery<SessionKeyDoc>(COLLECTIONS.sessionKeys, DOCUMENT_ID, ids);
  const rows = approvalBatchRows(migration, keys.data);
  const counts = approvalBatchCounts(rows);
  return (
    <Card withBorder radius="md" padding="lg" data-testid="approval-batch">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Approval batch</Text>
        {rows.length > 0 ? (
          <Text size="sm" c="dimmed">
            {counts.total} keys: {counts.enabled} enabled, {counts.pending} pending
            {counts.revoked ? `, ${counts.revoked} revoked` : ""}
            {counts.unknown ? `, ${counts.unknown} unread` : ""}
          </Text>
        ) : null}
      </Group>
      <Text size="sm" c="dimmed" mb="md" maw={860}>
        Every key the owner approves in the one biometric batch of installingTransferKeys: the
        transfer keys, the source&apos;s forward keys (never blocking the transfer) and the
        successor&apos;s card keys (a card-cohort source waits on them unless waived).
      </Text>
      {migration.approvalSessionKeys === undefined ? (
        <Text size="sm" c="dimmed">
          Not projected: the document predates wallet-service 0.77.0.
        </Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          No batch yet: it is stamped when the migration reaches installingTransferKeys.
        </Text>
      ) : (
        <>
          {keys.error ? <ErrorState error={keys.error} /> : null}
          <Table withTableBorder verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Purpose</Table.Th>
                <Table.Th>Key</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Lives on</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Valid until</Table.Th>
                <Table.Th>Enabled on chain</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={`${row.purpose}-${row.sessionKeyId}`}>
                  <Table.Td>
                    <Tooltip label={row.purpose} withArrow>
                      <Text size="sm">{row.label}</Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <RelatedLink entity="sessionKey" id={row.sessionKeyId} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {row.type}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Badge
                        color={RELATION_COLOR[row.relation]}
                        variant="light"
                        radius="sm"
                        tt="none"
                      >
                        {row.relation}
                      </Badge>
                      <RelatedLink entity="wallet" id={row.account} />
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {row.state ? <StatusBadge state={row.state} /> : <Text c="dimmed">unread</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Timestamp value={row.validUntil} />
                  </Table.Td>
                  <Table.Td>
                    <Timestamp value={row.enabledOnChainAt} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Card>
  );
}

const LOCATION_DISPLAY: Record<ObligationLocation, { label: string; color: string }> = {
  successor: { label: "on the successor", color: "teal" },
  source: { label: "still on the source", color: "orange" },
  elsewhere: { label: "on another wallet", color: "gray" },
  notLoaded: { label: "not in the loaded rows", color: "gray" },
};

function rowError(error: unknown): string {
  if (error instanceof AdminApiError)
    return `${error.message} (HTTP ${error.status}${error.code ? `, ${error.code}` : ""})`;
  return error instanceof Error ? error.message : String(error);
}

function IdList({
  ids,
  entity,
}: {
  ids: readonly string[];
  entity: "settlement" | "settlementObligation";
}) {
  if (ids.length === 0) return <Text c="dimmed">none</Text>;
  return (
    <Group gap="xs">
      {ids.map((id) => (
        <RelatedLink key={id} entity={entity} id={id} />
      ))}
    </Group>
  );
}

/**
 * The card cohort (wallet-service 0.77.0): the snapshot's card-settlement inventory (read from the
 * Spanner row: Firestore does not project it), the waiver, the successor's open obligations, and
 * what is still on the source (open obligations, live CCTP settlements, live holds), with the alert
 * markers those imply.
 */
export function CardSettlementPanel({ migration }: { migration: Migration }) {
  const { getIdToken } = useAuth();
  const id = migration.id ?? migration._docId;
  const row = useQuery({
    queryKey: ["walletMigrationRow", id, migration.updatedAt],
    queryFn: async ({ signal }) => fetchWalletMigrationRow(id, await getIdToken(false), signal),
    staleTime: 30_000,
    retry: false,
  });
  const successor = migration.newWallet ?? null;
  const successorObligations = useFirestoreQuery<SettlementObligationDoc>(
    COLLECTIONS.settlementObligations,
    {
      filters: [["walletId", "==", successor ?? ""]],
      orderBy: [["createdAt", "desc"]],
      limit: 100,
    },
    !!successor,
  );
  const sourceObligations = useFirestoreQuery<SettlementObligationDoc>(
    COLLECTIONS.settlementObligations,
    {
      filters: [["walletId", "==", migration.oldWallet]],
      orderBy: [["createdAt", "desc"]],
      limit: 100,
    },
  );
  const sourceSettlements = useFirestoreQuery<SettlementDoc>(COLLECTIONS.settlements, {
    filters: [["walletId", "==", migration.oldWallet]],
    orderBy: [["createdAt", "desc"]],
    limit: 100,
  });
  const sourceHolds = useFirestoreQuery<HoldDoc>(COLLECTIONS.holds, {
    filters: [["wallet", "==", migration.oldWallet]],
    orderBy: [["createdAt", "desc"]],
    limit: 100,
  });

  const snapshot = row.data?.snapshot ?? null;
  const inventory = snapshot?.cardSettlement ?? null;
  const markers = cardCohortMarkers({
    migration,
    snapshot,
    sourceObligations: sourceObligations.data,
    sourceSettlements: sourceSettlements.data,
    sourceHolds: sourceHolds.data,
  });
  const locations = inventory
    ? inventoryObligationLocations({
        inventory,
        sourceWallet: migration.oldWallet,
        successorWallet: successor,
        obligations: [...successorObligations.data, ...sourceObligations.data],
      })
    : null;
  const openOnSuccessor = successorObligations.data.filter(isOpenObligation);
  const cctpOnSource = liveSettlements(sourceSettlements.data);
  const waiver = row.data?.cardKeysWaiver ?? null;
  const failures = [
    ["Successor obligations", successorObligations.error],
    ["Source obligations", sourceObligations.error],
    ["Source CCTP settlements", sourceSettlements.error],
    ["Source holds", sourceHolds.error],
  ].filter((entry): entry is [string, Error] => entry[1] instanceof Error);

  return (
    <Card withBorder radius="md" padding="lg" data-testid="card-settlement">
      <Group justify="space-between" mb="xs" align="flex-start">
        <Group gap="sm">
          <Text fw={600}>Card settlement</Text>
          <AlertMarkers markers={markers} />
        </Group>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconRefresh size={14} />}
          loading={row.isFetching}
          onClick={() => void row.refetch()}
        >
          Re-read the row
        </Button>
      </Group>
      <Text size="sm" c="dimmed" mb="md" maw={860}>
        Card settlement moves with the user: snapshotting freezes and quiesces the source&apos;s
        card settlement, records this inventory, and rekeyingEntities moves every open obligation
        (with its legs, approved payment and card-auth holds) to the successor and checks it against
        the inventory. Post-burn CCTP settlements stay on the source on purpose. The snapshot is
        read from GET /walletMigrations/{"{id}"} (the Spanner row): Firestore does not project it.
      </Text>
      <Stack gap="md">
        {failures.length > 0 ? (
          <Alert color="red" variant="light" title="Some card-settlement reads failed">
            {failures.map(([label, error]) => (
              <Text key={label} size="sm">
                {label}: {error.message}
              </Text>
            ))}
          </Alert>
        ) : null}
        {row.isLoading ? (
          <LoadingState label="Reading the migration row…" />
        ) : row.error ? (
          <Alert color="orange" variant="light" title="Could not read the migration row">
            {rowError(row.error)}
          </Alert>
        ) : !snapshot ? (
          <Text size="sm" c="dimmed">
            No snapshot yet: it is taken in snapshotting (and again after an owner retry).
          </Text>
        ) : (
          <Stack gap="sm">
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              <FieldRow
                label="Old Main at snapshot"
                value={<Money micro={snapshot.oldMainBalanceMicroUsdc} />}
              />
              <FieldRow
                label="Old Reserve at snapshot"
                value={<Money micro={snapshot.oldReserveBalanceMicroUsdc} />}
              />
              <FieldRow
                label="Block"
                value={<Mono value={snapshot.blockNumber?.toString()} copy={false} />}
              />
              <FieldRow label="Read at" value={<Timestamp value={snapshot.readAt} absolute />} />
            </SimpleGrid>
            {snapshot.cardSettlement === undefined ? (
              <Text size="sm" c="dimmed">
                No card-settlement inventory: the snapshot was taken before wallet-service 0.77.0.
              </Text>
            ) : !inventory ? (
              <Text size="sm" c="dimmed">
                No card-settlement inventory recorded on this snapshot.
              </Text>
            ) : (
              <>
                <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm" data-testid="inventory">
                  <FieldRow
                    label="Live Reserve position"
                    value={<Money micro={inventory.liveReservePositionMicroUsdc} fw={600} />}
                  />
                  <FieldRow
                    label="On-chain Reserve"
                    value={<Money micro={inventory.onChainReserveMicroUsdc} />}
                  />
                  <FieldRow
                    label="Moved by an included leg"
                    value={<Money micro={inventory.reserveMovedByIncludedLegMicroUsdc} />}
                  />
                  <FieldRow
                    label="Reserve shortfall"
                    value={
                      <Money
                        micro={inventory.reserveShortfallMicroUsdc}
                        c={inventory.reserveShortfallMicroUsdc > 0 ? "red" : undefined}
                        fw={inventory.reserveShortfallMicroUsdc > 0 ? 700 : undefined}
                      />
                    }
                  />
                  <FieldRow label="Scheduled top-up legs" value={String(inventory.scheduledLegs)} />
                  <FieldRow
                    label="Live card-auth holds"
                    value={<Money micro={inventory.liveCardAuthHoldsMicroUsdc} />}
                  />
                </SimpleGrid>
                <Table withTableBorder verticalSpacing="xs" data-testid="inventory-obligations">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Open obligation at the snapshot</Table.Th>
                      <Table.Th>State then</Table.Th>
                      <Table.Th>Position</Table.Th>
                      <Table.Th>Now</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {inventory.openObligations.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Text size="sm" c="dimmed">
                            None: the source had no open obligation.
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      inventory.openObligations.map((o) => {
                        const where = locations?.get(o.id);
                        const display = LOCATION_DISPLAY[where?.location ?? "notLoaded"];
                        return (
                          <Table.Tr key={o.id}>
                            <Table.Td>
                              <RelatedLink entity="settlementObligation" id={o.id} />
                            </Table.Td>
                            <Table.Td>
                              <StatusBadge state={o.state} />
                            </Table.Td>
                            <Table.Td>
                              <Money
                                micro={o.positionMicroUsdc}
                                size="sm"
                                c={o.positionMicroUsdc < 0 ? "red" : undefined}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Group gap={4} wrap="nowrap">
                                <Badge color={display.color} variant="light" radius="sm" tt="none">
                                  {display.label}
                                </Badge>
                                {where?.state ? <StatusBadge state={where.state} /> : null}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })
                    )}
                  </Table.Tbody>
                </Table>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                  <FieldRow
                    label="Post-burn settlements (an assertion: always empty)"
                    value={<IdList ids={inventory.postBurnSettlements} entity="settlement" />}
                  />
                  <FieldRow
                    label="Exhausted releases (an operator's)"
                    value={<IdList ids={inventory.exhaustedItems} entity="settlementObligation" />}
                  />
                  <FieldRow
                    label="Legacy negative obligations"
                    value={
                      <IdList
                        ids={inventory.legacyNegativeObligations}
                        entity="settlementObligation"
                      />
                    }
                  />
                </SimpleGrid>
              </>
            )}
            {snapshot.leftBehind.length > 0 ? (
              <Table withTableBorder verticalSpacing="xs" data-testid="left-behind">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Left behind (not moved)</Table.Th>
                    <Table.Th>Token</Table.Th>
                    <Table.Th>Account</Table.Th>
                    <Table.Th>Amount (base units)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {snapshot.leftBehind.map((a) => (
                    <Table.Tr key={`${a.asset}-${a.address}`}>
                      <Table.Td>{a.asset}</Table.Td>
                      <Table.Td>
                        <Mono value={a.token ?? "native"} short={!!a.token} copy={false} />
                      </Table.Td>
                      <Table.Td>
                        <Mono value={a.address} short />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {a.amount}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : null}
          </Stack>
        )}

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <FieldRow
            label="Card keys waived"
            value={<Timestamp value={migration.cardKeysWaivedAt} absolute />}
          />
          <FieldRow label="Waived by" value={<RelatedLink entity="user" id={waiver?.by} />} />
          <FieldRow label="Waiver reason" value={waiver?.reason || "—"} />
        </SimpleGrid>

        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Successor&apos;s open obligations ({openOnSuccessor.length})
          </Text>
          {!successor ? (
            <Text size="sm" c="dimmed">
              No successor stamped yet.
            </Text>
          ) : openOnSuccessor.length === 0 ? (
            <Text size="sm" c="dimmed">
              None open on the successor.
            </Text>
          ) : (
            <Table withTableBorder verticalSpacing="xs" data-testid="successor-obligations">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Obligation</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Position</Table.Th>
                  <Table.Th>Payment</Table.Th>
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {openOnSuccessor.map((o) => (
                  <Table.Tr key={o._docId}>
                    <Table.Td>
                      <RelatedLink entity="settlementObligation" id={o._docId} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge state={o.state} />
                    </Table.Td>
                    <Table.Td>
                      <Money micro={obligationPositionMicroUsdc(o)} size="sm" />
                    </Table.Td>
                    <Table.Td>
                      <RelatedLink entity="payment" id={o.sourcePayment} />
                    </Table.Td>
                    <Table.Td>
                      <Timestamp value={o.updatedAt} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={600}>
            CCTP settlements still on the source ({cctpOnSource.length})
          </Text>
          {cctpOnSource.length === 0 ? (
            <Text size="sm" c="dimmed">
              None in flight on the source.
            </Text>
          ) : (
            <Table withTableBorder verticalSpacing="xs" data-testid="source-cctp">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Settlement</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Obligation</Table.Th>
                  <Table.Th>Burn (HyperEVM)</Table.Th>
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {cctpOnSource.map((s) => (
                  <Table.Tr key={s._docId}>
                    <Table.Td>
                      <RelatedLink entity="settlement" id={s._docId} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge state={s.state} />
                    </Table.Td>
                    <Table.Td>
                      <Money micro={s.amountMicroUsdc} size="sm" />
                    </Table.Td>
                    <Table.Td>
                      <RelatedLink entity="settlementObligation" id={s.obligationId} />
                    </Table.Td>
                    <Table.Td>
                      <ExternalMono value={s.burnTxHash} href={txUrl(s.burnTxHash)} />
                    </Table.Td>
                    <Table.Td>
                      <Timestamp value={s.updatedAt} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * The earn cohort (earn-service 0.10.0): the migration's drain (`walletMigrationDrains/{id}`):
 * per-vault attempts, the next attempt, the latest attempt's earn operation, and the re-deposit offer
 * with its dismissal. Nothing to show when the source had no Earn position.
 */
export function EarnDrainPanel({ migration }: { migration: Migration }) {
  const now = useNow();
  const id = migration.id ?? migration._docId;
  const drain = useFirestoreDoc<WalletMigrationDrainDoc>(COLLECTIONS.walletMigrationDrains, id);
  const vaults = drain.data?.vaults ?? [];
  const operations = useFirestoreInQuery<EarnOperationDoc>(
    COLLECTIONS.earnOperations,
    DOCUMENT_ID,
    vaults.map((v) => v.operation),
  );
  const opById = new Map(operations.data.map((o) => [o._docId, o]));
  const markers = drainMarkers({ drain: drain.data, migration });
  const d = drain.data;

  return (
    <Card withBorder radius="md" padding="lg" data-testid="earn-drain">
      <Group gap="sm" mb="xs">
        <Text fw={600}>Earn drain</Text>
        {d ? <StatusBadge state={d.state} /> : null}
        <AlertMarkers markers={markers} />
      </Group>
      <Text size="sm" c="dimmed" mb="md" maw={860}>
        drainingPositions asks earn to withdraw every HyperLend position to the source&apos;s Main
        with its earn key (one attempt at a time per vault, retried with a backoff and no cap)
        before the snapshot. A re-deposit is never automatic: the owner gets a one-tap offer on the
        target wallet.
      </Text>
      {drain.loading ? (
        <LoadingState label="Loading the drain…" />
      ) : drain.error ? (
        isPermissionError(drain.error) ? (
          <Text size="sm" c="dimmed">
            walletMigrationDrains cannot be read in this environment yet (earn-service 0.10.0, its
            rules and index).
          </Text>
        ) : (
          <ErrorState error={drain.error} />
        )
      ) : !d ? (
        <Text size="sm" c="dimmed">
          No earn drain for this migration: the source had no Earn position, the migration has not
          reached drainingPositions, or it predates earn-service 0.10.0.
        </Text>
      ) : (
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <FieldRow label="State" value={DRAIN_STATE_LABELS[d.state] ?? d.state} />
            <FieldRow label="Drained" value={<Money micro={d.drainedMicroUsdc} fw={600} />} />
            <FieldRow label="Source" value={<RelatedLink entity="wallet" id={d.wallet} />} />
            <FieldRow
              label="Successor"
              value={<RelatedLink entity="wallet" id={d.successorWallet} />}
            />
            <FieldRow
              label="Re-deposit offered"
              value={<Money micro={d.redepositOfferedMicroUsdc} />}
            />
            <FieldRow
              label="Re-deposit target"
              value={<RelatedLink entity="wallet" id={d.redepositTargetWallet} />}
            />
            <FieldRow
              label="Offer dismissed"
              value={<Timestamp value={d.redepositDismissedAt} absolute />}
            />
            <FieldRow
              label="Completed / abandoned"
              value={<Timestamp value={d.completedAt ?? d.abandonedAt} absolute />}
            />
          </SimpleGrid>
          {operations.error ? <ErrorState error={operations.error} /> : null}
          <Table withTableBorder verticalSpacing="xs" data-testid="drain-vaults">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Vault</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Attempts</Table.Th>
                <Table.Th>Latest attempt</Table.Th>
                <Table.Th>Next attempt</Table.Th>
                <Table.Th>Drained</Table.Th>
                <Table.Th>Re-deposit</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vaults.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text size="sm" c="dimmed">
                      No vault: nothing to withdraw (or a tombstone written after the migration
                      failed).
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                vaults.map((v) => {
                  const status = drainVaultStatus(v, d.state, now);
                  const op = v.operation ? opById.get(v.operation) : undefined;
                  return (
                    <Table.Tr key={v.vaultId ?? v.vault}>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <RelatedLink entity="vault" id={v.vault} label={v.vault} />
                          <Text size="xs" c="dimmed">
                            {v.protocol}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={status.color} variant="light" radius="sm" tt="none">
                          {status.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{v.attempts}</Table.Td>
                      <Table.Td>
                        <Stack gap={0}>
                          <Group gap={4} wrap="nowrap">
                            <RelatedLink entity="earnOperation" id={v.operation} />
                            {op ? <StatusBadge state={op.state} /> : null}
                          </Group>
                          {op?.failureReason ? (
                            <Text size="xs" c="red" maw={260}>
                              {op.failureReason}
                            </Text>
                          ) : null}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Timestamp value={v.nextAttemptAt} />
                      </Table.Td>
                      <Table.Td>
                        <Money micro={v.drainedMicroUsdc} size="sm" />
                      </Table.Td>
                      <Table.Td>
                        {v.redepositedAt ? (
                          <Text size="sm">
                            landed <Timestamp value={v.redepositedAt} />
                          </Text>
                        ) : (
                          <Money micro={v.redepositOfferedMicroUsdc} size="sm" />
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Card>
  );
}

/** The migration detail's cohort panels, between its fields and its related lists. */
export function WalletMigrationPanels({ migration }: { migration: Migration }) {
  return (
    <>
      <ApprovalBatchPanel migration={migration} />
      <CardSettlementPanel migration={migration} />
      <EarnDrainPanel migration={migration} />
    </>
  );
}
